import type { MeshMetrics } from './meshMetrics'
import {
  ACTIVE_RATE_CARD,
  calculateShipping,
  type ShippingResult,
  type ShippingRateCard,
  type ShippingService
} from './shipping'

/**
 * AZONNALI ARBECSLES A BONGESZOBEN.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT NEM VARUNK A SZELETELESRE
 *
 * A pontos arat a VOXORA Forge adja meg, valodi szeletelessel. De arra
 * varni percek, es a Forge nem is mindig el: a gep alhat, lehet
 * halozatkimaradas, es a sor is lehet hosszu.
 *
 * A latogato viszont ELSO PILLANATBAN arat akar latni. Ha nem kap, elmegy.
 * Ezert ez a becsles a bongeszoben fut, a modell geometriajabol, es SEMMI
 * kulso feltetele nincs - egy internetkapcsolat nelkuli Forge mellett is
 * mukodik.
 * ────────────────────────────────────────────────────────────────────
 *
 * A keplet NEM "terfogat szorozva egy szammal". A haromszoghalobol a
 * terfogat MELLETT a feluletet is kiszamoljuk, es abbol kulon becsuljuk a
 * hejat es a kitoltest:
 *
 *   hej    = felulet x falvastagsag       (a kerulet-vonalak anyaga)
 *   belso  = terfogat - hej
 *   anyag  = (hej + belso x kitoltes) x korrekcio
 *
 * Egy vekony falu dobozra es egy tomor kockara ugyanaz a keplet mukodik,
 * mert a felulet/terfogat arany maga hordozza a kulonbseget.
 *
 * A parameterek VERZIOZOTTAK es a szerverrol jonnek
 * (`estimation_params`). Igy a becsles idovel pontosithato a korabbi
 * Forge-szeletelesek eredmenyeibol, a mar kiadott ajanlatok szamai
 * pedig visszakovethetok maradnak - mindegyiken rajta van, melyik
 * verzioval keszult.
 */

export interface QualityParams {
  /** Kerulet-szam. */
  walls: number
  /** Vonalszelesseg mm-ben. */
  line: number
  /** Kitoltes aranya (0-1). */
  infill: number
  layer: number
  /** A profil sebessege a 0,2 mm-es alaphoz kepest. */
  time_factor: number
}

export interface EstimationParams {
  shell_correction: number
  seconds_per_cm3_at_02mm: number
  support: { threshold: number; max_extra: number }
  material: Record<string, { density: number }>
  quality: Record<string, QualityParams>
  pricing: {
    material_per_gram: number
    energy_per_hour: number
    minimum_order: number
    round_to: number
  }
  express: { pct: number; min_huf: number }
  /**
   * SIMITASI (IRONING) FELAR.
   *
   * A SIMITOTT tetelek gyartasi ertekenek szazaleka, also hatarral. A
   * Forge ugyanezekkel az ertekekkel szamol (`PricingConfig`), kulonben a
   * "becsult" es a "pontos" ar nem lenne osszehasonlithato.
   */
  ironing: { pct: number; min_huf: number }
  confidence: { min_samples: number; band_pct: number }
}

/*
  A SZALLITASI DIJ NEM ITT LAKIK.

  Regen egy `shipping: { parcel_point: 1490, home: 1990 }` allt itt. Ez ket
  dolgot rontott el: fuggetlen volt a csomag SULYATOL, es fuggetlen volt a
  valasztott FUTARTOL - egy 30 grammos es egy 12 kilos kuldemeny ugyanannyiba
  kerult. Helyette a verziozott rate card szamol (`lib/shipping.ts`), a
  tenyleges tomeg es a konkret szolgaltatas alapjan.
*/

/**
 * BEEPITETT TARTALEK PARAMETEREK.
 *
 * Ha a szerver nem valaszol, a becsles ATTOL MEG ELKESZUL - csak a
 * beepitett ertekekkel. Ez az egesz "azonnali ar" igeret lelke: egyetlen
 * halozati hiba sem allithatja meg.
 *
 * Az ertekek egyeznek az `estimation_params` 1. verziojaval (lasd a
 * 20260811100700 migraciot). Ha ott valtozik valami, ide is at kell
 * vezetni - kulonben a tartalek csendben elavulna.
 */
export const FALLBACK_PARAMS: EstimationParams = {
  shell_correction: 0.86,
  seconds_per_cm3_at_02mm: 284,
  support: { threshold: 0.25, max_extra: 0.18 },
  material: { PETG: { density: 1.27 }, PLA: { density: 1.24 } },
  quality: {
    'petg-gyors': { walls: 2, line: 0.42, infill: 0.1, layer: 0.3, time_factor: 0.92 },
    'petg-altalanos': { walls: 3, line: 0.42, infill: 0.2, layer: 0.2, time_factor: 1.0 },
    'petg-reszletes': { walls: 3, line: 0.4, infill: 0.2, layer: 0.12, time_factor: 1.08 },
    'petg-ultra': { walls: 4, line: 0.4, infill: 0.2, layer: 0.08, time_factor: 1.18 },
    'petg-eros': { walls: 4, line: 0.45, infill: 0.15, layer: 0.25, time_factor: 0.98 },
    'petg-vizallo': { walls: 5, line: 0.42, infill: 0.25, layer: 0.2, time_factor: 1.05 },
    'pla-gyors': { walls: 2, line: 0.42, infill: 0.1, layer: 0.3, time_factor: 0.88 },
    'pla-altalanos': { walls: 3, line: 0.42, infill: 0.2, layer: 0.2, time_factor: 0.95 },
    'pla-reszletes': { walls: 3, line: 0.4, infill: 0.2, layer: 0.12, time_factor: 1.02 },
    'pla-ultra': { walls: 4, line: 0.4, infill: 0.2, layer: 0.08, time_factor: 1.12 }
  },
  pricing: {
    material_per_gram: 8.5,
    energy_per_hour: 100,
    minimum_order: 2500,
    round_to: 5
  },
  express: { pct: 0.3, min_huf: 3000 },
  ironing: { pct: 0.15, min_huf: 900 },
  confidence: { min_samples: 12, band_pct: 0.18 }
}

export const FALLBACK_VERSION = 2

/** A gep epitesi tere. A tipust szandekosan nem nevezzuk meg sehol. */
export const BUILD_VOLUME = { x: 260, y: 260, z: 260 }

// ─────────────────────────── egy tetel becslese ───────────────────────────

export interface ItemEstimateInput {
  metrics: MeshMetrics
  material: string
  qualityId: string
  quantity: number
  scale: number
  /** Kert-e a latogato simitast erre a tetelre. Fizetos extra. */
  ironing?: boolean
}

export interface ItemEstimate {
  grams: number
  seconds: number
  /**
   * Simitva keszul-e.
   *
   * Azert kell a KIMENETBEN is, mert a felar alapja csak a simitott
   * tetelek gyartasi erteke - az osszesitesnek tudnia kell, melyik tetel
   * szamit bele.
   */
  ironing: boolean
  /**
   * A darab MEGFORGATVA sem fer bele az epitesi terbe.
   *
   * FONTOS: a bongeszo NEM utasit el modellt azert, mert az EREDETI
   * allasaban tul nagy - elforgatva simán elferhet. Ezt a mezot ezert csak
   * akkor allitjuk igazra, ha a HAROM meret kozul a legkisebb is nagyobb az
   * epitesi ter legnagyobb meretenel. A vegso szot a Forge mondja ki, az
   * automatikus tajolas utan.
   */
  definitelyOversize: boolean
}

export function estimateItem(
  input: ItemEstimateInput,
  params: EstimationParams = FALLBACK_PARAMS
): ItemEstimate {
  const qty = Math.max(1, Math.floor(input.quantity))
  const scale = input.scale > 0 ? input.scale : 1

  const quality = params.quality[input.qualityId] ?? FALLBACK_PARAMS.quality['petg-altalanos']
  const density = (params.material[input.material] ?? { density: 1.27 }).density

  // A meretezes KOBOSEN hat a terfogatra es NEGYZETESEN a feluletre.
  const volumeMm3 = input.metrics.volumeMm3 * scale ** 3
  const surfaceMm2 = input.metrics.surfaceMm2 * scale ** 2

  const wallThickness = quality.walls * quality.line
  const shellMm3 = Math.min(surfaceMm2 * wallThickness, volumeMm3)
  const interiorMm3 = Math.max(0, volumeMm3 - shellMm3)

  const extrudedMm3 = (shellMm3 + interiorMm3 * quality.infill) * params.shell_correction

  /*
    Tamaszigeny kozelitese.

    A bongeszo nem tud tulnyulast szamolni, de a befoglalo doboz
    KITOLTOTTSEGE arulkodo: egy nagyon "levegos" modell (kis terfogat, nagy
    doboz) alatt tobb tamasz kell. Ez SZANDEKOSAN enyhe korrekcio - a
    pontos ertek a szeletelesbol jon, es nem akarunk tulbecsulni.
  */
  const box =
    input.metrics.size.x * input.metrics.size.y * input.metrics.size.z * scale ** 3
  const fill = box > 0 ? volumeMm3 / box : 1
  const supportExtra =
    fill < params.support.threshold
      ? params.support.max_extra * (1 - fill / params.support.threshold)
      : 0

  const extrudedCm3 = (extrudedMm3 * (1 + supportExtra)) / 1000

  const gramsEach = extrudedCm3 * density
  // Vekonyabb reteg -> aranyosan hosszabb ido ugyanarra az anyagra.
  const secondsEach =
    extrudedCm3 *
    params.seconds_per_cm3_at_02mm *
    (0.2 / quality.layer) *
    quality.time_factor

  const size = input.metrics.size
  const smallest = Math.min(size.x, size.y, size.z) * scale
  const biggestBuild = Math.max(BUILD_VOLUME.x, BUILD_VOLUME.y, BUILD_VOLUME.z)

  return {
    grams: gramsEach * qty,
    seconds: secondsEach * qty,
    ironing: input.ironing === true,
    definitelyOversize: smallest > biggestBuild
  }
}

// ─────────────────────────── a teljes ajanlat ───────────────────────────

export interface QuoteEstimate {
  grams: number
  seconds: number
  materialCost: number
  energyCost: number
  production: number
  minimumApplied: boolean
  minimumTopUp: number
  /** Simitasi felar. 0, ha egyetlen tetelt sem kertek simitva. */
  ironingHuf: number
  expressHuf: number
  /**
   * GYARTASI RESZOSSZEG: gyartas + minimum + simitas + Expressz, szallitas NELKUL.
   * Az ingyenes szallitas kuszobet EZ dönti el.
   */
  productionSubtotal: number
  /** Az ugyfelnek felszamitott szallitas. 0, ha ingyenes vagy szemelyes. */
  shippingHuf: number
  /** Ingyenes szallitas lepett-e eletbe. */
  freeShipping: boolean
  /**
   * A teljes szallitasi szamitas, BELSO mezokkel egyutt.
   *
   * `null`, ha meg nincs valasztott atveteli mod. Ha `available: false`, a
   * valasztott szolgaltatas ennel a sulynal nem elerheto - a felulet ilyenkor
   * masikat kerjen, ne szamoljon "kozeliot".
   */
  shipping: ShippingResult | null
  total: number
  /** Becslesi sav also/felso hatara, ha van eleg historikus adat. */
  lowHuf: number | null
  highHuf: number | null
}

/** Magyar kerekitesi szabaly szerint a legkozelebbi lepeskozre. */
export function roundToStep(value: number, step: number): number {
  return step <= 0 ? Math.round(value) : Math.round(value / step) * step
}

/**
 * A TELJES ajanlat becsult ara.
 *
 * A sorrend UGYANAZ, mint a Forge vegleges arazasaban
 * (`shared/pricing.ts` -> `calculateQuoteTotal`), kulonben a "becsult" es
 * a "pontos" ar nem lenne osszehasonlithato, es az admin eltéres-nezet
 * ertelmetlen szamokat mutatna.
 *
 *   gyartas -> minimum -> simitas -> Expressz -> szallitas -> kerekites
 */
export function estimateQuote(
  items: ItemEstimate[],
  options: {
    /**
     * A valasztott atveteli mod. `null`, amig a latogato nem valasztott -
     * ilyenkor a szallitas 0, es a vegosszeg mellett ezt ki is irjuk.
     */
    service: ShippingService | null
    express: boolean
    /** Hany osszehasonlito mintank van mar - ettol fugg, mutatunk-e savot. */
    sampleCount?: number
  },
  params: EstimationParams = FALLBACK_PARAMS,
  card: ShippingRateCard = ACTIVE_RATE_CARD
): QuoteEstimate {
  const grams = items.reduce((s, i) => s + i.grams, 0)
  const seconds = items.reduce((s, i) => s + i.seconds, 0)

  const materialCost = grams * params.pricing.material_per_gram
  const energyCost = (seconds / 3600) * params.pricing.energy_per_hour
  const production = materialCost + energyCost

  const minimumApplied = production < params.pricing.minimum_order
  const minimumTopUp = minimumApplied ? params.pricing.minimum_order - production : 0
  const afterMinimum = production + minimumTopUp

  /*
    SIMITASI FELAR.

    Az alap CSAK a simitott tetelek gyartasi erteke: ha ot modellbol
    egyet kernek simitva, nem a teljes ajanlat terhelodik. A `> 0`
    vizsgalat nem formalitas - nelkule egy simitas nelkuli ajanlat is
    megkapna a 900 Ft-os also hatart.

    ELTERES A FORGE-TOL: ott az alap a simitott TANYEROK erteke, es azok
    masodpercei mar tartalmazzak a simitasi meneteket is. Itt a bongeszo
    a tetelek becsult idejevel szamol, amiben ez nincs benne - a becsles
    ezert egy kicsit alabecsuli a simitott munkakat. Szandekosan nem
    "potoljuk" egy kitalalt szazalekkal: a simitas ideje a FELSO FELULET
    nagysagaval aranyos, nem a darab terfogataval, tehat egy atalany
    korrekcio egy magas, keskeny darabnal ugyanugy tevedne, csak masik
    iranyba.
  */
  const ironingBase = items
    .filter((i) => i.ironing)
    .reduce(
      (sum, i) =>
        sum +
        i.grams * params.pricing.material_per_gram +
        (i.seconds / 3600) * params.pricing.energy_per_hour,
      0
    )

  const ironingHuf =
    ironingBase > 0
      ? Math.max(Math.round(ironingBase * params.ironing.pct), params.ironing.min_huf)
      : 0

  /*
    Az Expressz a gyartasra ES a simitasra vonatkozik: mindketto a sajat
    gepunkon toltott ido. A szallitas kimarad - a futart nem mi gyorsitjuk.
  */
  const expressHuf = options.express
    ? Math.max(
        Math.round((afterMinimum + ironingHuf) * params.express.pct),
        params.express.min_huf
      )
    : 0

  /*
    A SZALLITAS A GYARTASI RESZOSSZEG UTAN JON.

    Fontos a sorrend: az ingyenes szallitas kuszobet a gyartas + minimum +
    Expressz osszege donti el, a szallitas NELKUL. Ha a szallitast is
    beleszamolnank, egy dragabb futar valasztasa hozna ingyenes szallitast -
    ami korben forgo szabaly lenne.
  */
  const productionSubtotal = afterMinimum + ironingHuf + expressHuf

  const shipping =
    options.service &&
    calculateShipping(
      { service: options.service, modelGrams: grams, productionSubtotalHuf: productionSubtotal },
      card
    )

  const shippingHuf = shipping?.available ? shipping.customerShippingHuf : 0
  const freeShipping = shipping?.available ? shipping.freeShipping : false

  const total = roundToStep(productionSubtotal + shippingHuf, params.pricing.round_to)

  /*
    BECSLESI SAV.

    Csak akkor mutatunk tartomanyt, ha van eleg historikus minta. Amig
    nincs, egyetlen szam all ott "kb." jelolessel: egy hamis pontossagu
    tartomany rosszabb lenne, mint a semmi - azt sugallna, hogy tudjuk a
    szoras nagysagat, pedig nem.
  */
  const enough = (options.sampleCount ?? 0) >= params.confidence.min_samples
  const band = params.confidence.band_pct

  return {
    grams,
    seconds,
    materialCost,
    energyCost,
    production,
    minimumApplied,
    minimumTopUp,
    ironingHuf,
    expressHuf,
    productionSubtotal,
    shippingHuf,
    freeShipping,
    shipping: shipping ?? null,
    total,
    lowHuf: enough ? roundToStep(total * (1 - band), params.pricing.round_to) : null,
    highHuf: enough ? roundToStep(total * (1 + band), params.pricing.round_to) : null
  }
}
